import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import UserService from "src/Services/API/user.service";
import PasswordUpgradeService from "src/Services/API/password-upgrade.service";
import { handleError, PASSWORD_HINT, validatePassword } from "src/Common/function";
import { FieldLabel, ModalShell, PrimaryButton } from "src/Pages/WO/Components/upload/uploadModalUI";

function PasswordField({ label, value, onChange, error, autoComplete, onEnter }) {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter in any field submits, as in a form. Not while an input method
          // (e.g. Korean) is still composing a character.
          if (e.key === "Enter" && !e.nativeEvent.isComposing) onEnter?.();
        }}
        className={`h-[44px] px-3 border rounded-[6px] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] ${
          error ? "border-red-500" : "border-[#E5E8F3]"
        }`}
      />
    </div>
  );
}

// Shown after a sign-in whose password predates the password rules (see
// PasswordUpgradeService), and it can't be dismissed: the app stays blocked
// until the password has been changed. Right after sign-in it uses the
// password just entered; only when that is gone (a reload) does it ask for it.
function WeakPasswordModal() {
  const [shown, setShown] = useState(false);
  const [signInPassword, setSignInPassword] = useState(() => PasswordUpgradeService.getSignInPassword());
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (PasswordUpgradeService.isDue(UserService.getCurrentUser()?.id)) {
      setShown(true);
    }
  }, []);

  const changeClicked = async () => {
    // Enter can call this again while saving; the disabled button doesn't stop it.
    if (saving) return;
    const current = signInPassword ?? currentPassword;
    const result = validatePassword(newPassword);
    const nextErrors = {
      current: !current,
      new: !result.success,
      confirm: newPassword !== confirmPassword,
    };
    setErrors(nextErrors);
    if (nextErrors.current) return toast("Please enter your current password");
    if (nextErrors.new) return toast(result.message);
    if (nextErrors.confirm) return toast("Passwords don't match");

    setSaving(true);
    try {
      await PasswordUpgradeService.changePassword(current, newPassword);
      toast("Password changed");
      PasswordUpgradeService.clear();
      setShown(false);
    } catch (error) {
      // e.g. a new password the server finds too common. Should it reject the
      // current password, ask for it instead of retrying the remembered one.
      if (/current password/i.test(error?.response?.data?.message ?? "")) {
        setSignInPassword(null);
      }
      handleError(error);
    } finally {
      setSaving(false);
    }
  };

  if (!shown) return null;

  // No onClose: it can't be dismissed until the password has been changed.
  return (
    <ModalShell
      title="Choose a stronger password"
      width="w-[480px]"
      footer={
        <PrimaryButton onClick={changeClicked} loading={saving} loadingLabel="Changing...">
          Change Password
        </PrimaryButton>
      }
    >
      <div className="px-6 py-5 flex flex-col gap-4">
        <p className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
          Your password is easy to guess. Set a new one to continue. {PASSWORD_HINT}
        </p>

        {signInPassword == null && (
          <PasswordField
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            error={errors.current}
            autoComplete="current-password"
            onEnter={changeClicked}
          />
        )}
        <PasswordField
          label="New password"
          value={newPassword}
          onChange={setNewPassword}
          error={errors.new}
          autoComplete="new-password"
          onEnter={changeClicked}
        />
        <PasswordField
          label="Confirm new password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={errors.confirm}
          autoComplete="new-password"
          onEnter={changeClicked}
        />
      </div>
    </ModalShell>
  );
}

export default WeakPasswordModal;
